import tinycolor from 'tinycolor2';

/**
 * Converts a hexadecimal color code to RGB format.
 *
 * @param {string} hexCode - The hexadecimal color code to convert.
 * @returns {Object|null} An object containing the RGB values (r, g, b) if the conversion is successful, or null if the input is invalid.
 */
export const hexToRgb = (hexCode) => {
  const color = tinycolor(hexCode).toRgb();
  if (color.r === undefined || color.g === undefined || color.b === undefined) {
    return null;
  }
  return { r: color.r, g: color.g, b: color.b };
};

/**
 * Converts RGB color values to a hexadecimal color code.
 *
 * @param {Object} color - An object containing the RGB color values.
 * @param {number} color.r - The red color value (0-255).
 * @param {number} color.g - The green color value (0-255).
 * @param {number} color.b - The blue color value (0-255).
 * @returns {string} The hexadecimal color code.
 */
export const rgbToHex = ({ r, g, b }) => {
  return tinycolor({ r, g, b }).toHexString();
};

/**
 * Blend RGBA to RGB.
 * @param {Object} blendingColor - RGBA color wanted to transform to regular RBG with no alpha.
 * @param {Object} bgColor - RGB background color code underneath desired blending color.
 */
export const blendAlpha = ({ r, g, b, a = 0 }, asHex = false, bgColor = { r: 255, g: 255, b: 255 }) => {
  const alpha = 1 - a;
  const result = {
    r: Math.round((a * (r / 255) + alpha * (bgColor.r / 255)) * 255),
    g: Math.round((a * (g / 255) + alpha * (bgColor.g / 255)) * 255),
    b: Math.round((a * (b / 255) + alpha * (bgColor.b / 255)) * 255),
  };

  if (asHex) {
    return rgbToHex(result);
  }

  return result;
};

/**
 * Calculates the contrast color based on the given hex color.
 *
 * @param {string} hex - The hex color value.
 * @returns {string} The contrast color (either '#ffffff' or '#000000').
 */
export const getContrastColor = (hex) => {
  const color = tinycolor(hex);
  const bestADAColorContrast = tinycolor.mostReadable(color, ['#ffffff', '#000000']);
  return bestADAColorContrast.toHexString();
};

/**
 * Adjusts a color (preserving hue) so it reaches a minimum contrast ratio against a background color.
 * Used for theme-driven UI indicators (e.g. focus rings) where the source color is arbitrary
 * (an org's configurable primary color) and may not meet WCAG non-text contrast on its own.
 *
 * The background's luminance only decides which direction to try *first*. A mid-luminance
 * background (e.g. a tint near 0.5 luminance) can be unreachable in the preferred direction —
 * lightening tops out at white, which may still fall short of minRatio — so both directions are
 * evaluated before giving up. SO-3775.
 *
 * @param {string} hex - The color to make accessible.
 * @param {string} [backgroundHex] - The background the color will be shown against.
 * @param {number} [minRatio] - The minimum contrast ratio to satisfy (3 for WCAG 1.4.11 non-text contrast).
 * @returns {string} A hex color meeting minRatio contrast against backgroundHex, or the more
 *   readable of black/white if no hue-preserving adjustment reaches minRatio.
 */
export const getAccessibleColor = (hex, backgroundHex = '#ffffff', minRatio = 3) => {
  const background = tinycolor(backgroundHex);
  if (tinycolor.readability(hex, background) >= minRatio) {
    return tinycolor(hex).toHexString();
  }

  const preferDarken = background.getLuminance() > 0.5;
  const directions = preferDarken ? ['darken', 'lighten'] : ['lighten', 'darken'];

  for (const direction of directions) {
    for (let step = 1; step <= 100; step++) {
      const adjusted = tinycolor(hex)[direction](step);
      if (tinycolor.readability(adjusted, background) >= minRatio) {
        return adjusted.toHexString();
      }
    }
  }

  return tinycolor.mostReadable(background, ['#000000', '#ffffff']).toHexString();
};
